import React, { useEffect } from "react";
import "@/App.css";
import { BrowserRouter, Routes, Route, useLocation } from "react-router-dom";
import { Toaster } from "sonner";
import { AppProvider } from "@/context/AppContext";
import { installContentProtection } from "@/lib/contentProtection";
import Home from "@/pages/Home";
import Plans from "@/pages/Plans";
import PlanDetail from "@/pages/PlanDetail";
import Login from "@/pages/Login";
import Register from "@/pages/Register";
import Cart from "@/pages/Cart";
import CheckoutSuccess from "@/pages/CheckoutSuccess";
import Purchases from "@/pages/Purchases";
import About from "@/pages/About";
import AuthCallback from "@/pages/AuthCallback";
import CustomDesign from "@/pages/CustomDesign";
import Services from "@/pages/Services";
import Blog from "@/pages/Blog";
import BlogPost from "@/pages/BlogPost";
import Contact from "@/pages/Contact";
import PlanCollections from "@/pages/PlanCollections";
import Wishlist from "@/pages/Wishlist";
import ForgotPassword from "@/pages/ForgotPassword";
import ResetPassword from "@/pages/ResetPassword";
import VerifyEmail from "@/pages/VerifyEmail";
import ComparisonDrawer from "@/components/ComparisonDrawer";
import CompareFloatingBar from "@/components/CompareFloatingBar";

// Admin
import AdminDashboard from "@/pages/admin/AdminDashboard";
import AdminPlansList from "@/pages/admin/AdminPlansList";
import AdminPlanForm from "@/pages/admin/AdminPlanForm";
import AdminBulkImport from "@/pages/admin/AdminBulkImport";
import AdminBlogList from "@/pages/admin/AdminBlogList";
import AdminBlogEditor from "@/pages/admin/AdminBlogEditor";
import AdminPlaceholder from "@/pages/admin/AdminPlaceholder";
import SettingsPage from "@/pages/admin/SettingsPage";
import RolePermissions from "@/pages/admin/RolePermissions";
import AdminCustomers from "@/pages/admin/AdminCustomers";
import AdminCustomerDetail from "@/pages/admin/AdminCustomerDetail";
import AdminOrders from "@/pages/admin/AdminOrders";
import AdminOrderDetail from "@/pages/admin/AdminOrderDetail";
import AdminBranding from "@/pages/admin/AdminBranding";
import AdminStyles from "@/pages/admin/AdminStyles";
import AdminHomepage from "@/pages/admin/AdminHomepage";
import AdminCategories from "@/pages/admin/AdminCategories";
import AdminCollections from "@/pages/admin/AdminCollections";
import AdminCurrency from "@/pages/admin/AdminCurrency";
import ScrollToTop from "@/components/ScrollToTop";
import AdminLandingPages from "@/pages/admin/AdminLandingPages";
import AdminLandingPageEditor from "@/pages/admin/AdminLandingPageEditor";
import LandingPage from "@/pages/LandingPage";
import AdminMediaLibrary from "@/pages/admin/AdminMediaLibrary";
import AdminReports from "@/pages/admin/AdminReports";
import AdminRefunds from "@/pages/admin/AdminRefunds";
import AdminDownloads from "@/pages/admin/AdminDownloads";
import AdminBackups from "@/pages/admin/AdminBackups";
import AdminSystemLogs from "@/pages/admin/AdminSystemLogs";
import PlanVersionHistory from "@/pages/admin/PlanVersionHistory";
import UserManagement from "@/pages/UserManagement";
import ActivityLog from "@/pages/ActivityLog";
import Enquiries from "@/pages/Enquiries";
import AdminProjects from "@/pages/admin/AdminProjects";
import AdminProjectDetail from "@/pages/admin/AdminProjectDetail";
import AdminClients from "@/pages/admin/AdminClients";
import AdminClientDetail from "@/pages/admin/AdminClientDetail";
import AdminLeads from "@/pages/admin/AdminLeads";
import AdminLeadDetail from "@/pages/admin/AdminLeadDetail";
import ComingSoon from "@/pages/ComingSoon";
import FloorPlans from "@/pages/FloorPlans";
import { Navigate as _Navigate } from "react-router-dom";
import Privacy from "@/pages/legal/Privacy";
import Terms from "@/pages/legal/Terms";
import Refund from "@/pages/legal/Refund";
import NotFound from "@/pages/NotFound";

const AppRouter = () => {
  const location = useLocation();
  if (location.hash?.includes("session_id=")) {
    return <AuthCallback />;
  }

  return (
    <Routes>
      {/* Public */}
      <Route path="/" element={<Home />} />
      <Route path="/plans" element={<Plans />} />
      {/* Iter57 — SEO Landing Pages (must come BEFORE /plans/:id catch-all) */}
      <Route path="/plans/style/:slug" element={<LandingPage facetType="style" />} />
      <Route path="/plans/category/:slug" element={<LandingPage facetType="category" />} />
      <Route path="/plans/collection/:slug" element={<LandingPage facetType="collection" />} />
      <Route path="/plans/:id" element={<PlanDetail />} />
      <Route path="/plan-collections" element={<PlanCollections />} />
      <Route path="/custom-design" element={<CustomDesign />} />
      <Route path="/services" element={<Services />} />
      <Route path="/blog" element={<Blog />} />
      <Route path="/blog/:slug" element={<BlogPost />} />
      <Route path="/contact" element={<Contact />} />
      <Route path="/wishlist" element={<Wishlist />} />
      <Route path="/login" element={<Login />} />
      <Route path="/register" element={<Register />} />
      <Route path="/forgot-password" element={<ForgotPassword />} />
      <Route path="/reset-password" element={<ResetPassword />} />
      <Route path="/verify-email" element={<VerifyEmail />} />
      <Route path="/cart" element={<Cart />} />
      <Route path="/checkout/success" element={<CheckoutSuccess />} />
      <Route path="/purchases" element={<Purchases />} />
      <Route path="/about" element={<About />} />
      <Route path="/auth/callback" element={<AuthCallback />} />
      {/* Iter60A — Floor Plans marketplace. /coming-soon 301-equivalent
          client redirect for SEO backward-compat. */}
      <Route path="/floor-plans" element={<FloorPlans />} />
      <Route path="/coming-soon" element={<_Navigate to="/floor-plans" replace />} />
      <Route path="/privacy" element={<Privacy />} />
      <Route path="/terms" element={<Terms />} />
      <Route path="/refund" element={<Refund />} />

      {/* Admin */}
      <Route path="/admin" element={<AdminDashboard />} />
      <Route path="/admin/dashboard" element={<AdminDashboard />} />

      {/* Plans */}
      <Route path="/admin/plans" element={<AdminPlansList />} />
      {/* Iter60A — Floor Plans admin workflow reuses AdminPlansList with a
          `channel` prop that filters to sales_channel in {floor_plan, both}. */}
      <Route path="/admin/plans/floor" element={<AdminPlansList channel="floor_plan" />} />
      <Route path="/admin/plans/new" element={<AdminPlanForm mode="create" />} />
      <Route path="/admin/plans/bulk-import" element={<AdminBulkImport />} />
      <Route path="/admin/plans/:id/edit" element={<AdminPlanForm mode="edit" />} />
      <Route path="/admin/plans/:plan_id/versions" element={<PlanVersionHistory />} />

      {/* Categories / Collections */}
      <Route path="/admin/categories" element={<AdminCategories />} />
      <Route path="/admin/collections" element={<AdminCollections />} />
      {/* Iter58 — Settings → Currency */}
      <Route path="/admin/currency" element={<AdminCurrency />} />
      {/* Iter57 — SEO Landing Pages CMS */}
      <Route path="/admin/landing" element={<AdminLandingPages />} />
      <Route path="/admin/landing/:id/edit" element={<AdminLandingPageEditor />} />

      {/* Orders */}
      <Route path="/admin/orders" element={<AdminOrders />} />
      <Route path="/admin/orders/:session_id" element={<AdminOrderDetail />} />
      <Route path="/admin/payments" element={<AdminOrders />} />
      <Route path="/admin/refunds" element={<AdminRefunds />} />
      <Route path="/admin/downloads" element={<AdminDownloads />} />

      {/* Customers */}
      <Route path="/admin/customers" element={<AdminCustomers />} />
      <Route path="/admin/customers/:user_id" element={<AdminCustomerDetail />} />
      <Route path="/admin/customers/purchases" element={<AdminOrders />} />
      <Route path="/admin/customers/wishlists" element={<AdminPlaceholder title="Customer wishlists" description="Wishlist analytics — which plans are most saved." phase="Phase 5" />} />

      {/* Enquiries */}
      <Route path="/admin/enquiries" element={<Enquiries />} />

      {/* Projects (Phase 6) */}
      <Route path="/admin/projects" element={<AdminProjects />} />
      <Route path="/admin/projects/:id" element={<AdminProjectDetail />} />

      {/* Clients (Phase 7A) */}
      <Route path="/admin/clients" element={<AdminClients />} />
      <Route path="/admin/clients/:id" element={<AdminClientDetail />} />

      {/* Leads (Phase 7A.1 — CRM auto-link) */}
      <Route path="/admin/leads" element={<AdminLeads />} />
      <Route path="/admin/leads/:id" element={<AdminLeadDetail />} />

      {/* Team */}
      <Route path="/admin/users" element={<UserManagement />} />
      <Route path="/admin/roles" element={<RolePermissions />} />
      <Route path="/admin/activity" element={<ActivityLog />} />

      {/* Content */}
      <Route path="/admin/blog" element={<AdminBlogList />} />
      <Route path="/admin/blog/new" element={<AdminBlogEditor mode="create" />} />
      <Route path="/admin/blog/:id/edit" element={<AdminBlogEditor mode="edit" />} />
      <Route path="/admin/testimonials" element={<AdminPlaceholder title="Testimonials" description="Manage customer testimonials shown on the marketing site." phase="Phase 4+" />} />
      <Route path="/admin/faqs" element={<AdminPlaceholder title="FAQs" description="Manage frequently asked questions." phase="Phase 4+" />} />
      <Route path="/admin/featured" element={<AdminPlaceholder title="Featured Plans" description="Curate the featured plans row." phase="Phase 4" />} />

      {/* Media */}
      <Route path="/admin/media/images" element={<AdminMediaLibrary />} />
      <Route path="/admin/media/floor-plans" element={<AdminMediaLibrary />} />
      <Route path="/admin/media/pdfs" element={<AdminMediaLibrary />} />
      <Route path="/admin/media/videos" element={<AdminMediaLibrary />} />
      <Route path="/admin/media/documents" element={<AdminMediaLibrary />} />

      {/* Settings */}
      <Route path="/admin/settings" element={<SettingsPage />} />
      <Route path="/admin/branding" element={<AdminBranding />} />
      <Route path="/admin/styles" element={<AdminStyles />} />
      <Route path="/admin/homepage" element={<AdminHomepage />} />

      {/* Reports */}
      <Route path="/admin/reports/sales" element={<AdminReports />} />
      <Route path="/admin/reports/revenue" element={<AdminReports />} />
      <Route path="/admin/reports/popular" element={<AdminReports />} />
      <Route path="/admin/reports/customers" element={<AdminReports />} />
      <Route path="/admin/reports/downloads" element={<AdminReports />} />
      {/* Backup */}
      <Route path="/admin/backup" element={<AdminBackups />} />
      <Route path="/admin/restore" element={<AdminBackups />} />
      <Route path="/admin/logs" element={<AdminSystemLogs />} />

      <Route path="*" element={<NotFound />} />
    </Routes>
  );
};

function App() {
  useEffect(() => { installContentProtection(); }, []);
  return (
    <div className="App">
      <AppProvider>
        <BrowserRouter>
          <ScrollToTop />
          <AppRouter />
          <ComparisonDrawer />
          <CompareFloatingBar />
          <Toaster position="top-right" richColors />
        </BrowserRouter>
      </AppProvider>
    </div>
  );
}

export default App;
